import { Picture as PictureType } from '@/@types/common'
import { Box, BoxProps } from '@mantine/core'
import clsx from 'clsx'
import { chunk } from 'lodash-es'
import { MouseEvent } from 'react'
import styles from './ImageGrid.module.css'
import ImageGridItem from './ImageGridItem'

export interface ImageGridProps<DataItem extends PictureType = PictureType> extends BoxProps {
	data: DataItem[]
	mode?: 'full' | 'compact' | 'compact-sm'
	onItemClick?: (
		data: DataItem,
		index: number,
		e: MouseEvent<Element, globalThis.MouseEvent>
	) => void
}

export default function ImageGrid<DataItem extends PictureType = PictureType>({
	data,
	onItemClick,
	mode = 'full',
	className,
	...props
}: ImageGridProps<DataItem>) {
	if (mode === 'full' && data.length === 1 && data[0]) {
		const item = data[0]
		const width = item.width ?? 0
		const height = item.height ?? 0
		const getOrientation = (width: number, height: number) => {
			if (width > height) {
				return 'landscape'
			}
			if (width < height) {
				return 'portrait'
			}
			return 'square'
		}
		const getSize = (width: number, height: number) => {
			const aspectRatio = width > 0 && height > 0 ? width / height : undefined
			const orientation = getOrientation(width, height)

			if (orientation === 'portrait') {
				const width = 260
				const height = aspectRatio ? width / aspectRatio : undefined
				return {
					width: `${width}px`,
					height: height ? `${height}px` : undefined,
					maxHeight: '607px',
					aspectRatio,
				}
			}

			if (orientation === 'landscape') {
				const height = 260
				const width = aspectRatio ? height * aspectRatio : undefined
				return {
					width: width ? `${width}px` : undefined,
					height: `${height}px`,
					aspectRatio,
				}
			}

			return {
				width: '260px',
				height: '260px',
				aspectRatio,
			}
		}

		const size = getSize(width, height)
		return (
			<ImageGridItem
				data={item}
				onClick={(e: MouseEvent<HTMLImageElement>) => onItemClick?.(item, 0, e)}
				data-orientation={getOrientation(width, height)}
				data-grid-mode={mode}
				className={styles.picture}
				style={
					mode === 'full'
						? {
								aspectRatio: size.aspectRatio,
								height: size.height,
								width: size.width,
								maxHeight: size.maxHeight,
								maxWidth: '100%',
							}
						: {}
				}
			/>
		)
	}

	const rows = chunk(data, 3)

	return (
		<Box className={clsx(styles.root, className)} {...props}>
			{rows.map((row, rowIndex) => (
				<div
					className={styles.row}
					data-grid-mode={mode}
					data-size={row.length}
					key={`${rowIndex}-${row.map((item) => item.key).join('-')}`}
				>
					{row.map((item, itemIndexInRow) => {
						const itemIndex = rowIndex * 3 + itemIndexInRow

						return (
							<div
								key={`${item.key ?? item.smallPicUrl ?? item.picUrl}-${itemIndex}`}
								className={styles.col}
							>
								<ImageGridItem
									data={item}
									onClick={(e: MouseEvent<HTMLImageElement>) => onItemClick?.(item, itemIndex, e)}
									className={styles.picture}
									data-mode="grid-item"
									data-grid-mode={mode}
								/>
							</div>
						)
					})}
				</div>
			))}
		</Box>
	)
}
